/**
 * Works out what the character is doing, moment to moment.
 *
 * **Tool calls cannot be made mandatory.** `LiveConnectConfig` has no
 * `toolConfig`, so `functionCallingConfig.mode = ANY` — the only API-level way
 * to force a call — isn't reachable from a Live session at all. And it would be
 * the wrong lever anyway: ANY constrains the model to predicting function calls
 * *only*, which for an audio session means no narration. So a prompt is the
 * strongest pressure available, and a prompt can always be ignored.
 *
 * The transcript is therefore the guarantee, not a fallback:
 *
 * - **Transcript (always on).** Each sentence gets one pose: the option it
 *   names, or the thinking pose if it's a question. Dense, deterministic, and
 *   it covers the whole video — a speech model always speaks.
 * - **Tool calls (additive).** Deliberate beats the words can't reveal, like
 *   dropping the hands for a verdict. They fill gaps where the transcript has
 *   nothing to say; on a conflict the transcript wins, because it is reading
 *   what was actually said.
 *
 * Both are timestamped by PCM byte position, so they share one clock and land
 * at exact offsets in the generated audio.
 */
import type { CaptionWord, PoseKind, PoseSegment } from "./types";

/**
 * Raise the arm slightly before the name is spoken. A presenter gestures into a
 * point, they don't react to their own words.
 */
const POSE_LEAD_MS = 350;

/** Shorter than this reads as a flicker, so it gets absorbed by its neighbour. */
const MIN_POSE_MS = 700;

/**
 * The video opens on the character standing neutral, before a word is spoken.
 *
 * This is bought with real silence at the head of the narration (`pcm.ts` writes
 * it, so every cue is already stamped past it) rather than by delaying the first
 * pose. There is no slack to delay into: the model speaks from its first sample,
 * and the scripted opening puts two short sentences back to back, so stealing
 * the hold out of the first one would leave that pose under `MIN_POSE_MS` and it
 * would be merged away — the opening point would vanish instead of the video
 * gaining a beat. Must stay above `MIN_POSE_MS`, or the hold itself is merged.
 */
export const OPENING_HOLD_MS = 900;

/**
 * A tool call this close to a transcript cue is describing the same beat, so it
 * is dropped rather than fighting it. Roughly one sentence.
 */
const TOOL_MERGE_WINDOW_MS = 1_500;

export interface PoseEvent {
	pose: PoseKind;
	atMs: number;
}

export function buildPoseSegments({
	toolEvents,
	words,
	thisLabel,
	thatLabel,
	durationMs,
}: {
	toolEvents: PoseEvent[];
	words: CaptionWord[];
	thisLabel: string;
	thatLabel: string;
	durationMs: number;
}): { segments: PoseSegment[]; toolCuesUsed: number } {
	const spoken = eventsFromTranscript({ words, thisLabel, thatLabel });
	// Only the calls that land in a gap survive. Taking the model's word over
	// the transcript wherever both fire was a real regression: two sloppy calls
	// (say, pointing at the same option twice) used to replace the spoken choreography
	// and the character would barely move for the whole video.
	const extra = toolEvents.filter(
		(tool) =>
			!spoken.some(
				(event) => Math.abs(event.atMs - tool.atMs) < TOOL_MERGE_WINDOW_MS,
			),
	);

	const events = [...spoken, ...extra].sort((a, b) => a.atMs - b.atMs);

	return {
		segments: segmentsFromEvents({ events, durationMs }),
		toolCuesUsed: extra.length,
	};
}

/**
 * Turn what was said into pose events.
 *
 * Sentence by sentence, because the unit of a gesture is a sentence:
 * - a question becomes a thinking beat for its whole sentence (a question that
 *   happens to name an option is still a question, not a point at it);
 * - otherwise the first name mentioned raises that arm.
 *
 * Labels are matched on their significant words so "Stable Diffusion" is found
 * inside "…but Stable Diffusion's ecosystem…". Tokens of one or two letters are
 * dropped so a label like "Go" or "C" doesn't match half the sentence — those
 * fall back to a whole-word match instead.
 */
function eventsFromTranscript({
	words,
	thisLabel,
	thatLabel,
}: {
	words: CaptionWord[];
	thisLabel: string;
	thatLabel: string;
}): PoseEvent[] {
	const thisTokens = labelTokens({ label: thisLabel });
	const thatTokens = labelTokens({ label: thatLabel });
	if (thisTokens.length === 0 && thatTokens.length === 0) return [];

	const events: PoseEvent[] = [];

	for (const sentence of splitSentences({ words })) {
		const startMs = Math.max(0, sentence[0].startMs - POSE_LEAD_MS);

		if (sentence.some((word) => word.text.includes("?"))) {
			events.push({ pose: "thinking", atMs: startMs });
			continue;
		}

		// One pose per sentence, starting when the sentence does. Firing on the
		// matched word instead would put the gesture halfway through — and in a
		// short intro line like "And that is GitHub." it would leave too little
		// time before the next sentence for the pose to survive at all.
		let thisScore = 0;
		let thatScore = 0;
		for (const word of sentence) {
			const normalized = normalize({ text: word.text });
			if (!normalized) continue;
			// The longer match wins, because one label is very often a substring of
			// the other — Git/GitHub, Java/JavaScript, React/React Native. Scoring
			// "github" as a hit for both would silently drop every cue for the
			// longer name.
			thisScore = Math.max(
				thisScore,
				bestMatch({ text: normalized, tokens: thisTokens }),
			);
			thatScore = Math.max(
				thatScore,
				bestMatch({ text: normalized, tokens: thatTokens }),
			);
		}

		if (thisScore === thatScore) continue;
		events.push({ pose: thisScore > thatScore ? "this" : "that", atMs: startMs });
	}

	return events;
}

function splitSentences({ words }: { words: CaptionWord[] }): CaptionWord[][] {
	const sentences: CaptionWord[][] = [];
	let current: CaptionWord[] = [];

	for (const word of words) {
		current.push(word);
		if (/[.!?。！？…]["')\]]?$/.test(word.text)) {
			sentences.push(current);
			current = [];
		}
	}
	if (current.length > 0) sentences.push(current);

	return sentences;
}

/** Length of the longest token found in `text`, or 0 for no match. */
function bestMatch({
	text,
	tokens,
}: {
	text: string;
	tokens: string[];
}): number {
	let longest = 0;
	for (const token of tokens) {
		if (text.includes(token) && token.length > longest) longest = token.length;
	}
	return longest;
}

function labelTokens({ label }: { label: string }): string[] {
	const normalized = normalize({ text: label });
	if (!normalized) return [];

	const parts = normalized.split(/\s+/).filter((part) => part.length > 2);
	// Short labels ("Go", "C#") have no long token — match the whole thing.
	return parts.length > 0 ? parts : [normalized];
}

function normalize({ text }: { text: string }): string {
	return text
		.toLowerCase()
		.replace(/[^\p{L}\p{N}\s]/gu, "")
		.trim();
}

function segmentsFromEvents({
	events,
	durationMs,
}: {
	events: PoseEvent[];
	durationMs: number;
}): PoseSegment[] {
	if (durationMs <= 0) return [];

	const points: PoseEvent[] = [];
	for (const event of events) {
		// Nothing may start inside the opening hold. Tool calls already land past
		// it (they are stamped against the padded track), but a transcript cue
		// backdates itself by POSE_LEAD_MS and would otherwise gesture into the
		// silence.
		const atMs = Math.min(durationMs, Math.max(OPENING_HOLD_MS, event.atMs));
		const previous = points.at(-1);
		// Repeats are a no-op: the pose is already held.
		if (previous?.pose === event.pose) continue;
		if (previous && atMs <= previous.atMs) {
			points[points.length - 1] = { pose: event.pose, atMs: previous.atMs };
			continue;
		}
		points.push({ pose: event.pose, atMs });
	}

	// Whatever the character points at first, it points at from the moment the
	// narration starts. Left where the cue lands, the opening gesture arrives
	// late for two reasons that both read as the character being asleep: the
	// transcript stream needs a moment of audio before its first delta, so the
	// sentence looks later than it was spoken; and if the narrator opens on a
	// line that names neither option, the first cue is a whole sentence away.
	// Nothing is invented here — this only moves a gesture the narration already
	// asked for, onto the frame where speech begins.
	if (points.length > 0) points[0].atMs = OPENING_HOLD_MS;

	// The character has to be on screen from the very first frame, and the frames
	// up to the first cue are the opening hold.
	if (points.length === 0 || points[0].atMs > 0) {
		points.unshift({ pose: "idle", atMs: 0 });
		if (points[1]?.pose === "idle") points.splice(1, 1);
	}

	const segments: PoseSegment[] = points.map((point, index) => ({
		pose: point.pose,
		startMs: point.atMs,
		endMs: points[index + 1]?.atMs ?? durationMs,
	}));

	return mergeShortSegments({ segments, durationMs });
}

function mergeShortSegments({
	segments,
	durationMs,
}: {
	segments: PoseSegment[];
	durationMs: number;
}): PoseSegment[] {
	const merged: PoseSegment[] = [];

	for (const segment of segments) {
		const previous = merged.at(-1);
		const isTooShort = segment.endMs - segment.startMs < MIN_POSE_MS;
		// A sliver almost always means two names in one breath; holding the
		// earlier pose looks deliberate where a flick does not.
		if (previous && (isTooShort || previous.pose === segment.pose)) {
			previous.endMs = segment.endMs;
			continue;
		}
		merged.push({ ...segment });
	}

	// A too-short opening segment has no neighbour to fall back on, so let the
	// one after it start from zero instead of opening on a twitch.
	if (merged.length > 1 && merged[0].endMs - merged[0].startMs < MIN_POSE_MS) {
		merged[1].startMs = merged[0].startMs;
		merged.shift();
	}

	const last = merged.at(-1);
	if (last) last.endMs = durationMs;

	return merged.filter((segment) => segment.endMs > segment.startMs);
}
